import { z } from 'zod';
import { JSONContentSchema } from './tiptap';

export enum EditorMode {
  CREATE = 'create',
  EDIT = 'edit',
}

export const ACCEPTED_FILE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/bmp': ['.bmp'],
  'image/webp': ['.webp'],
  'image/svg+xml': ['.svg'],
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
  'video/x-msvideo': ['.avi'],
  'video/webm': ['.webm'],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc', '.docx'],
  'application/vnd.ms-excel': ['.xls', '.xlsx'],
  'application/vnd.ms-powerpoint': ['.ppt', '.pptx'],
  'audio/mpeg': ['.mp3'],
  'audio/wav': ['.wav'],
  'audio/ogg': ['.ogg'],
  'audio/flac': ['.flac'],
  'audio/aac': ['.aac'],
  'audio/webm': ['.weba'],
  'audio/mp4': ['.m4a'],
  'audio/x-ms-wma': ['.wma'],
};

export const FormFieldAnswerSchema = z.looseObject({
  fieldId: z.string(),
  textInputValue: z.string().optional(),
  selectedOptions: z.array(z.string()).optional(),
  additionalInput: z.string().optional(),
  correct: z.boolean().nullable().optional(),
  revisionNumber: z.number().optional(),
});

export type FormFieldAnswer = z.infer<typeof FormFieldAnswerSchema>;

// Corresponds to the 'options' array within a form field
export const FormFieldOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
  position: z.number(),
  value: z.string().optional(),
  isCorrect: z.boolean().optional(),
});

// Corresponds to a single field within a section
export const FormFieldSchema = z.object({
  id: z.string(),
  fieldType: z.enum(['TEXT', 'RADIO', 'SELECT']),
  helperText: z.string().nullable(),
  label: z.string(),
  multiline: z.boolean().default(false),
  placeholder: z.string().nullable(),
  position: z.number(),
  required: z.boolean(),
  fullWidthLayout: z.boolean().default(false),
  options: z.array(FormFieldOptionSchema).optional(),
});

// Corresponds to a section within a form slide
export const FormSectionSchema = z.object({
  id: z.string(),
  title: z.string(),
  position: z.number(),
  description: JSONContentSchema,
  fields: z.array(FormFieldSchema),
});

export type FormFieldOption = z.infer<typeof FormFieldOptionSchema>;
export type FormField = z.infer<typeof FormFieldSchema>;
export type FormSection = z.infer<typeof FormSectionSchema>;
